import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { TrainStaffOnDuty } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { Nullish } from "shared/utils/objectUtils";

type AltTransStopReference = {
  plcId: Nullish<string>;
  jbvId: string;
};

export type AltTransTrainStation = {
  reference: AltTransStopReference;
  name?: string | null;
};

export type AltTransTrainId = Omit<TrainIdentifier, "identifier"> & {
  id: string;
};

export type AltTransTrainRoute = {
  originStop: AltTransTrainStation;
  destinationStop: AltTransTrainStation;
  currentStop?: AltTransTrainStation | null;
  currentStopArrivalTime?: string | null;
};

export enum AltTransIncidentArrivalStatus {
  APPROACHING_INCIDENT = "APPROACHING_INCIDENT",
  AT_INCIDENT = "AT_INCIDENT",
  DEPARTED_INCIDENT = "DEPARTED_INCIDENT",
  UNKNOWN = "UNKNOWN",
}

export type AltTransIncidentArrival = {
  scheduledTime: string;
  estimatedTime?: string | null;
  actualTime?: string | null;
  stop: AltTransTrainStation;
  status: AltTransIncidentArrivalStatus;
};

type AltTransPassengerCount = {
  incidentStretchPassengerCount?: number | null;
};

export type AltTransTrainEvent = {
  id: string;
  type: TrainEventTypeEnum;
  // Extension from drops-backend
  label: string;
};

export enum AltTransMeasureType {
  NEXT_TRAIN = "NEXT_TRAIN",
  NO_COVERAGE = "NO_COVERAGE",
  OTHER_PROVIDER_REFERRAL = "OTHER_PROVIDER_REFERRAL",
  OTHER_TRAINS = "OTHER_TRAINS",
  REPLACEMENT_TRIP = "REPLACEMENT_TRIP",
  SHUTTLE = "SHUTTLE",
}

export type AltTransMeasure = {
  type: AltTransMeasureType;
};

export type AltTransIncidentTrain = {
  id: AltTransTrainId;
  trainRoute: AltTransTrainRoute;
  incidentArrival: AltTransIncidentArrival;
  passengerCount: AltTransPassengerCount;
  events: Array<AltTransTrainEvent>;
  measures: Array<AltTransMeasure>;
  // Extension from drops-backend
  trainStaffOnDuty: Array<TrainStaffOnDuty>;
};

type AltTransTrainsByEntryPoint = {
  entryPoint: AltTransTrainStation;
  trains: Array<AltTransIncidentTrain>;
};

export type AltTransIncidentTasks = {
  incidentId: string;
  trainsOutsideIncident: Array<AltTransTrainsByEntryPoint>;
  trainsAtIncident: Array<AltTransIncidentTrain>;
};

type AltTransPassengerPredictionBlock = {
  fromTime: string;
  passengersRequiringTransport: number;
  predictionBlockMinutes: bigint;
};

type AltTransStretchPrediction = {
  stops: Array<AltTransStopReference>;
  fromStop: AltTransStopReference;
  toStop: AltTransStopReference;
  predictions: Array<AltTransPassengerPredictionBlock>;
};

type AltTransPassengerPrediction = {
  aggregatedPredictions: Array<AltTransPassengerPredictionBlock>;
  stretchPredictions: Array<AltTransStretchPrediction>;
  totalAffectedPassengers: number;
};

export type AltTransIncidentPassengerPredictions = {
  prediction: AltTransPassengerPrediction;
  measureCardPrediction: Nullish<AltTransPassengerPrediction>;
  incidentStretchesPrediction: AltTransPassengerPrediction;
};

export type AltTransMeasureProgress = {
  stretchCoverages: Array<AltTransStretchCoverage>;
  totalCoverage: AltTransBusCoverage;
  peakPassengersRequiringTransport: number;
};

export type AltTransStretchCoverage = {
  totalStretchCoverage: AltTransBusCoverage;
  maxPassengers: number;
  fromStation: AltTransStopReference;
  toStation: AltTransStopReference;
  stretchName: string;
  busCoverageBlocks: Array<AltTransCoverageTimeBlock>;
};

type AltTransCoverageTimeBlock = {
  fromTime: string;
  predictionBlockMinutes: bigint;
  busCoverage: AltTransBusCoverage;
};

export type AltTransBusCoverage = {
  totalRequired: Nullish<number>;
  totalRequiredByMeasureCard: Nullish<number>;
  totalOrdered: number;
  totalConfirmed: number;
  totalOperational: number;
  stillRequired: Nullish<number>;
};

export type AltTransIncidentMeasurePlanStatus =
  | "NOT_PUBLISHED"
  | "PUBLISHED"
  | "RETRACTED";
